<script setup lang="ts">
/**
 * Quick-add Campaign form.
 *
 * Minimal surface: Name + Status + Severity + Notes. Fidelity defaults to
 * 70 (analyst-authored rows start at moderate fidelity). Attributed actor
 * is left to the full edit flow — the typeahead pattern in the epic doc is
 * nice but adds implementation scope we don't need for the acceptance bar.
 *
 * Ships the fast path first; typeahead actor picker can be a fast-follow
 * once we have real Oweis usage telling us whether it's worth the code.
 */
import { ref, onMounted, nextTick } from 'vue';
import apiClient from '@/api/client';
import QuickAddScopeLabel from './QuickAddScopeLabel.vue';

const emit = defineEmits<{ done: [] }>();

const name = ref('');
const status = ref('active');
const severity = ref('high');
const notes = ref('');

const saving = ref(false);
const errorMsg = ref<string | null>(null);
const lastSavedName = ref<string | null>(null);

const nameInput = ref<HTMLInputElement | null>(null);

onMounted(async () => {
  await nextTick();
  nameInput.value?.focus();
});

async function save(addAnother: boolean) {
  if (!name.value.trim()) return;
  saving.value = true;
  errorMsg.value = null;

  try {
    await apiClient.post('/campaigns', {
      name: name.value.trim(),
      status: status.value,
      severity: severity.value,
      fidelity: 70,
      description: notes.value.trim() || undefined,
    });

    if (addAnother) {
      lastSavedName.value = name.value.trim();
      name.value = '';
      notes.value = '';
      await nextTick();
      nameInput.value?.focus();
    } else {
      emit('done');
    }
  } catch (err: unknown) {
    const resp = (err as { response?: { data?: { message?: string | string[] } } })?.response;
    const m = resp?.data?.message;
    errorMsg.value = Array.isArray(m) ? m[0] : m ?? 'Save failed';
  } finally {
    saving.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
    e.preventDefault();
    save(e.shiftKey);
  }
}
</script>

<template>
  <form
    class="quick-add-campaign-form flex flex-col gap-3"
    data-qa-form="campaign"
    @keydown="onKeydown"
    @submit.prevent="save(false)"
  >
    <div class="quick-add-campaign-header flex flex-col gap-1">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-text-primary">Add Campaign</h2>
        <QuickAddScopeLabel />
      </div>
      <p v-if="lastSavedName" class="text-xs text-status-success">Saved: {{ lastSavedName }}</p>
    </div>

    <div v-if="errorMsg" class="text-sm text-status-error">{{ errorMsg }}</div>

    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Name</label>
        <input
          ref="nameInput"
          v-model="name"
          class="qa-input"
          placeholder="Operation CloudHopper"
          data-qa-field="name"
          autocomplete="off"
        />
      </div>

      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-text-muted">Status</label>
          <select v-model="status" class="qa-input" data-qa-field="status">
            <option value="active">Active</option>
            <option value="concluded">Concluded</option>
            <option value="dormant">Dormant</option>
            <option value="unconfirmed">Unconfirmed</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-text-muted">Severity</label>
          <select v-model="severity" class="qa-input" data-qa-field="severity">
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="info">Info</option>
          </select>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Notes (optional)</label>
        <textarea
          v-model="notes"
          class="qa-input"
          rows="2"
          placeholder="What you know so far"
          data-qa-field="notes"
        />
      </div>
    </div>

    <div class="flex items-center justify-end gap-2 pt-2">
      <button type="button" class="qa-btn-ghost" @click="emit('done')">Cancel</button>
      <button
        type="button"
        class="qa-btn-secondary"
        :disabled="!name.trim() || saving"
        data-qa-action="save-and-add"
        @click="save(true)"
      >
        Save &amp; Add Another
      </button>
      <button
        type="submit"
        class="qa-btn-primary"
        :disabled="!name.trim() || saving"
        data-qa-action="save"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.qa-input {
  background: var(--color-surface-700, #2a2a3e);
  border: 1px solid var(--color-border-default, #3a3a4e);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: var(--color-text-primary, #e5e5ea);
  font-size: 0.875rem;
  outline: none;
}
.qa-input:focus { border-color: var(--color-accent-blue, #3b82f6); }
.qa-btn-primary {
  background: var(--color-accent-blue, #3b82f6);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}
.qa-btn-primary:hover:not(:disabled) { opacity: 0.9; }
.qa-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.qa-btn-secondary {
  background: var(--color-surface-700, #2a2a3e);
  color: var(--color-text-primary, #e5e5ea);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid var(--color-border-default, #3a3a4e);
}
.qa-btn-secondary:hover:not(:disabled) { background: var(--color-surface-600, #3a3a4e); }
.qa-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
.qa-btn-ghost {
  color: var(--color-text-secondary, #a0a0aa);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}
.qa-btn-ghost:hover { background: var(--color-surface-700, #2a2a3e); }
</style>
